import type { CameraState, ViewportRect } from "./types"

const SPRING_RESPONSE_SECONDS = 0.4
const SPRING_OMEGA = (Math.PI * 2) / SPRING_RESPONSE_SECONDS
const POSITION_SETTLE_THRESHOLD = 0.1
const POSITION_VELOCITY_SETTLE_THRESHOLD = 2
const SCALE_SETTLE_THRESHOLD = 0.0001
const SCALE_VELOCITY_SETTLE_THRESHOLD = 0.002
const MOMENTUM_DECELERATION = 8
const MOMENTUM_STOP_VELOCITY = 8
const MAX_MOMENTUM_VELOCITY = 1800
const MAX_TICK_SECONDS = 0.1

export const CAMERA_MIN_SCALE = 0.02
export const CAMERA_MAX_SCALE = 3

type Motion = "spring" | "momentum" | null
export type CameraOperationResult = "settled" | "interrupted"

export class Camera {
  // Current presentation state
  x = 0
  y = 0
  scale = 1

  // Target state (for spring animation)
  private tx = 0
  private ty = 0
  private ts = 1

  // Velocities are expressed per second, making integration frame-rate independent.
  private vx = 0
  private vy = 0
  private vs = 0
  private motion: Motion = null
  private operationSequence = 0
  private currentOperation: number | null = null
  private readonly operationOutcomes = new Map<number, CameraOperationResult>()
  private readonly operationWaiters = new Map<
    number,
    Set<(result: CameraOperationResult) => void>
  >()

  animating = false

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: (sx - this.x) / this.scale,
      y: (sy - this.y) / this.scale,
    }
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: wx * this.scale + this.x,
      y: wy * this.scale + this.y,
    }
  }

  getVisibleRect(canvasWidth: number, canvasHeight: number): ViewportRect {
    const tl = this.screenToWorld(0, 0)
    const br = this.screenToWorld(canvasWidth, canvasHeight)
    return {
      x: tl.x,
      y: tl.y,
      width: br.x - tl.x,
      height: br.y - tl.y,
    }
  }

  getState(): CameraState {
    return { x: this.x, y: this.y, scale: this.scale }
  }

  // Instant move (no animation)
  setImmediate(x: number, y: number, scale: number): number {
    const operation = this.beginOperation()
    this.applyImmediate(x, y, scale)
    this.completeOperation(operation, "settled")
    return operation
  }

  // Animated move. Retargeting an existing spring starts from the current
  // presentation value and carries its velocity through the new trajectory.
  moveTo(x: number, y: number, scale: number): number {
    const carriesSpringVelocity = this.motion === "spring"
    const operation = this.beginOperation()
    if (!carriesSpringVelocity) {
      this.vx = 0
      this.vy = 0
      this.vs = 0
    }
    this.tx = x
    this.ty = y
    this.ts = clamp(scale, CAMERA_MIN_SCALE, CAMERA_MAX_SCALE)
    this.motion = "spring"
    this.animating = true
    return operation
  }

  // Stop at the current presentation value. Direct manipulation calls this
  // before applying deltas so grabbing a moving camera never jumps.
  stop() {
    if (this.currentOperation !== null) {
      this.completeOperation(this.currentOperation, "interrupted")
    }
    this.tx = this.x
    this.ty = this.y
    this.ts = this.scale
    this.clearMotion()
  }

  // Complete a programmatic animation immediately (used for reduced motion).
  finishAnimation() {
    if (this.motion === "spring" && this.currentOperation !== null) {
      const operation = this.currentOperation
      this.applyImmediate(this.tx, this.ty, this.ts)
      this.completeOperation(operation, "settled")
    } else {
      this.stop()
    }
  }

  // Pan by screen-space delta.
  pan(dx: number, dy: number) {
    this.stop()
    this.x += dx
    this.y += dy
    this.tx = this.x
    this.ty = this.y
  }

  // Continue a released pan with restrained exponential deceleration.
  startMomentum(vx: number, vy: number) {
    const speed = Math.hypot(vx, vy)
    if (!Number.isFinite(speed) || speed < MOMENTUM_STOP_VELOCITY) {
      this.stop()
      return
    }

    const restraint = Math.min(1, MAX_MOMENTUM_VELOCITY / speed)
    this.vx = vx * restraint
    this.vy = vy * restraint
    this.vs = 0
    this.tx = this.x
    this.ty = this.y
    this.ts = this.scale
    this.motion = "momentum"
    this.animating = true
  }

  // Zoom toward a screen point using the existing wheel-delta API.
  zoom(delta: number, sx: number, sy: number) {
    this.zoomBy(Math.exp(-delta * 0.002), sx, sy)
  }

  // Zoom by a multiplicative factor while keeping the screen anchor fixed.
  // Zoom is direct manipulation and therefore interrupts stale spring velocity.
  zoomBy(factor: number, sx: number, sy: number) {
    if (!Number.isFinite(factor) || factor <= 0) return
    this.stop()
    const newScale = clamp(
      this.scale * factor,
      CAMERA_MIN_SCALE,
      CAMERA_MAX_SCALE,
    )
    const ratio = newScale / this.scale

    this.x = sx - (sx - this.x) * ratio
    this.y = sy - (sy - this.y) * ratio
    this.scale = newScale
    this.tx = this.x
    this.ty = this.y
    this.ts = this.scale
  }

  // Animate a zoom button action around a screen-space anchor.
  zoomByAnimated(factor: number, sx: number, sy: number) {
    if (!Number.isFinite(factor) || factor <= 0) return
    this.stop()
    const scale = clamp(this.scale * factor, CAMERA_MIN_SCALE, CAMERA_MAX_SCALE)
    const ratio = scale / this.scale
    this.moveTo(sx - (sx - this.x) * ratio, sy - (sy - this.y) * ratio, scale)
  }

  fitView(
    bounds: ViewportRect,
    canvasWidth: number,
    canvasHeight: number,
    padding = 0.1,
    animate = true,
    offsetX = 0,
    offsetY = 0,
  ): number | null {
    if (bounds.width === 0 || bounds.height === 0) return null

    const pad = 1 + padding * 2
    const scaleX = canvasWidth / (bounds.width * pad)
    const scaleY = canvasHeight / (bounds.height * pad)
    const scale = clamp(
      Math.min(scaleX, scaleY),
      CAMERA_MIN_SCALE,
      CAMERA_MAX_SCALE,
    )

    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    const x = offsetX + canvasWidth / 2 - cx * scale
    const y = offsetY + canvasHeight / 2 - cy * scale

    return animate ? this.moveTo(x, y, scale) : this.setImmediate(x, y, scale)
  }

  waitForOperation(
    operation: number,
    signal?: AbortSignal,
  ): Promise<CameraOperationResult> {
    const outcome = this.operationOutcomes.get(operation)
    if (outcome) return Promise.resolve(outcome)
    if (this.currentOperation !== operation)
      return Promise.resolve("interrupted")
    if (signal?.aborted) {
      this.interruptOperation(operation)
      return Promise.resolve("interrupted")
    }
    return new Promise<CameraOperationResult>((resolve) => {
      let waiters = this.operationWaiters.get(operation)
      if (!waiters) {
        waiters = new Set()
        this.operationWaiters.set(operation, waiters)
      }
      const finish = (result: CameraOperationResult) => {
        signal?.removeEventListener("abort", onAbort)
        resolve(result)
      }
      const onAbort = () => this.interruptOperation(operation)
      waiters.add(finish)
      signal?.addEventListener("abort", onAbort, { once: true })
    })
  }

  // Returns true while motion remains. dt is seconds; the default preserves
  // compatibility for callers that previously ticked once per display frame.
  tick(dt = 1 / 60): boolean {
    if (!this.animating || this.motion === null) return false
    if (!Number.isFinite(dt) || dt <= 0) return true
    const elapsed = Math.min(dt, MAX_TICK_SECONDS)

    if (this.motion === "momentum") {
      const decay = Math.exp(-MOMENTUM_DECELERATION * elapsed)
      const displacement = (1 - decay) / MOMENTUM_DECELERATION
      this.x += this.vx * displacement
      this.y += this.vy * displacement
      this.vx *= decay
      this.vy *= decay

      if (Math.hypot(this.vx, this.vy) < MOMENTUM_STOP_VELOCITY) {
        this.stop()
        return false
      }
      return true
    }

    ;[this.x, this.vx] = springStep(this.x, this.vx, this.tx, elapsed)
    ;[this.y, this.vy] = springStep(this.y, this.vy, this.ty, elapsed)
    ;[this.scale, this.vs] = springStep(this.scale, this.vs, this.ts, elapsed)

    if (this.scale < CAMERA_MIN_SCALE || this.scale > CAMERA_MAX_SCALE) {
      this.scale = clamp(this.scale, CAMERA_MIN_SCALE, CAMERA_MAX_SCALE)
      this.vs = 0
    }

    const settled =
      Math.abs(this.vx) < POSITION_VELOCITY_SETTLE_THRESHOLD &&
      Math.abs(this.vy) < POSITION_VELOCITY_SETTLE_THRESHOLD &&
      Math.abs(this.vs) < SCALE_VELOCITY_SETTLE_THRESHOLD &&
      Math.abs(this.tx - this.x) < POSITION_SETTLE_THRESHOLD &&
      Math.abs(this.ty - this.y) < POSITION_SETTLE_THRESHOLD &&
      Math.abs(this.ts - this.scale) < SCALE_SETTLE_THRESHOLD

    if (settled) {
      const operation = this.currentOperation
      this.applyImmediate(this.tx, this.ty, this.ts)
      if (operation !== null) this.completeOperation(operation, "settled")
      return false
    }

    return true
  }

  private beginOperation(): number {
    if (this.currentOperation !== null) {
      this.completeOperation(this.currentOperation, "interrupted")
    }
    const operation = ++this.operationSequence
    this.currentOperation = operation
    return operation
  }

  private interruptOperation(operation: number) {
    if (this.currentOperation !== operation) return
    this.tx = this.x
    this.ty = this.y
    this.ts = this.scale
    this.clearMotion()
    this.completeOperation(operation, "interrupted")
  }

  private completeOperation(operation: number, result: CameraOperationResult) {
    if (this.currentOperation === operation) this.currentOperation = null
    this.operationOutcomes.set(operation, result)
    while (this.operationOutcomes.size > 128) {
      this.operationOutcomes.delete(this.operationOutcomes.keys().next().value!)
    }
    const waiters = this.operationWaiters.get(operation)
    this.operationWaiters.delete(operation)
    for (const resolve of waiters ?? []) resolve(result)
  }

  private applyImmediate(x: number, y: number, scale: number) {
    this.x = this.tx = x
    this.y = this.ty = y
    this.scale = this.ts = clamp(scale, CAMERA_MIN_SCALE, CAMERA_MAX_SCALE)
    this.clearMotion()
  }

  private clearMotion() {
    this.vx = 0
    this.vy = 0
    this.vs = 0
    this.motion = null
    this.animating = false
  }
}

// Exact critically-damped spring solution for a fixed target over dt.
function springStep(
  value: number,
  velocity: number,
  target: number,
  dt: number,
): [number, number] {
  const displacement = value - target
  const velocityTerm = velocity + SPRING_OMEGA * displacement
  const decay = Math.exp(-SPRING_OMEGA * dt)
  return [
    target + (displacement + velocityTerm * dt) * decay,
    (velocity - SPRING_OMEGA * velocityTerm * dt) * decay,
  ]
}

function clamp(v: number, min: number, max: number) {
  return v < min ? min : v > max ? max : v
}
